import React, { useEffect, useState } from 'react';
import { Alert, Button, Form, Input, Spin } from 'antd';
import { EditOutlined, SendOutlined } from '@ant-design/icons';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import CBModal from './CBModal';
import axiosInstance from '../variables/api/axiosInstance';
import { HAB, can } from '../variables/constants';
import { adresseUtilisable } from '../services/partieGarantie';

const GREEN = '#1a6b3c';
const { TextArea } = Input;

/**
 * Demande du procès-verbal au bureau homologue (sinistre TE).
 *
 * L'accident a eu lieu à l'étranger : le PV est là-bas. Ce popup montre le courriel
 * tel qu'il partira et fait CONFIRMER l'adresse - celle du référentiel est proposée,
 * l'agent la précise ou la corrige avant l'envoi. La demande ouvre une attente sur
 * l'homologue, visible dans les états partenaires, jusqu'à réception du PV.
 */
export default function DemandePvModal({ open, onClose, sinistre, onEnvoye }) {
  // Fenêtre dans la langue de l'agent ; l'objet et le message viennent du serveur,
  // rédigés dans la langue du bureau homologue.
  const { t } = useTranslation('documents');
  const [form] = Form.useForm();
  const [apercu, setApercu] = useState(null);
  const [chargement, setChargement] = useState(false);
  const [edition, setEdition] = useState(false);
  const [envoi, setEnvoi] = useState(false);
  const email = Form.useWatch('destinataireEmail', { form, preserve: true });
  const id = sinistre?.sinistreTrackingId;
  const peutEnvoyer = can(HAB.SINISTRE_UPDATE);

  useEffect(() => {
    if (!open || !id) return;
    setApercu(null);
    setChargement(true);
    axiosInstance.get(`/v1/sinistres/${id}/demande-pv/apercu`, { skipErrorToast: true })
      .then((r) => {
        const a = r.data?.data || null;
        setApercu(a);
        setEdition(a?.statutEnvoi !== 'ENVOYE' && peutEnvoyer);
      })
      .catch(() => { setApercu(null); onClose(); })
      .finally(() => setChargement(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, id]);

  const envoyer = async () => {
    const v = await form.validateFields();
    setEnvoi(true);
    try {
      const res = await axiosInstance.post(`/v1/sinistres/${id}/demande-pv/envoyer`, {
        destinataireEmail: v.destinataireEmail.trim(),
        objet: v.objet,
        corps: v.corps,
      });
      const message = res.data?.message || t('ecran.courrielDemande.demandeEnvoyee');
      if (/NON envoyé/i.test(message)) toast.warning(message); else toast.success(message);
      onEnvoye?.();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.message || t('ecran.courrielDemande.nonEnvoyee'));
    } finally {
      setEnvoi(false);
    }
  };

  const vers = t('ecran.courrielDemande.pv.vers', { nom: apercu?.destinataire || '' }).trim();
  const envoye = apercu?.statutEnvoi === 'ENVOYE';
  const adresseDifferente = apercu && !apercu.motifAdresse && email && email.trim() !== apercu.email;

  const pied = edition ? [
    <Button key="annuler" onClick={envoye ? () => setEdition(false) : onClose}>
      {envoye ? t('ecran.courrielDemande.revenir') : t('ecran.courrielDemande.pv.plusTard')}
    </Button>,
    <Button key="envoyer" type="primary" icon={<SendOutlined />} loading={envoi}
      disabled={!adresseUtilisable(email)} onClick={envoyer}
      style={{ background: GREEN, borderColor: GREEN }}>
      {envoye ? t('ecran.courrielDemande.renvoyer') : t('ecran.courrielDemande.pv.envoyerDemande')}
    </Button>,
  ] : [
    <Button key="fermer" onClick={onClose}>{t('ecran.courrielDemande.fermer')}</Button>,
    peutEnvoyer ? (
      <Button key="modifier" type="primary" icon={<EditOutlined />} onClick={() => setEdition(true)}
        style={{ background: GREEN, borderColor: GREEN }}>
        {t('ecran.courrielDemande.modifierRenvoyer')}
      </Button>
    ) : null,
  ].filter(Boolean);

  return (
    <CBModal
      title={t('ecran.courrielDemande.pv.titre', { vers })}
      open={open}
      onCancel={onClose}
      footer={pied}
      width={720}
      destroyOnClose
    >
      {chargement || !apercu ? (
        <div style={{ textAlign: 'center', padding: 32 }}><Spin /></div>
      ) : (
        <>
          {envoye && !edition && (
            <Alert type="success" showIcon style={{ marginBottom: 16, borderRadius: 10 }}
              message={t('ecran.courrielDemande.pv.envoyee', { vers })}
              description={[
                apercu.dateEnvoi ? t('ecran.courrielDemande.le', { date: dayjs(apercu.dateEnvoi).format(t('ecran.courrielDemande.formatDateHeure')) }) : null,
                apercu.reference ? t('ecran.courrielDemande.ref', { reference: apercu.reference }) : null,
                t('ecran.courrielDemande.pv.attentePv'),
              ].filter(Boolean).join(' · ')} />
          )}
          {!apercu.statutEnvoi && !edition && (
            <Alert type="info" showIcon style={{ marginBottom: 16, borderRadius: 10 }}
              message={t('ecran.courrielDemande.pasEncoreEnvoye')} />
          )}
          {apercu.statutEnvoi === 'ECHEC' && (
            <Alert type="warning" showIcon style={{ marginBottom: 16, borderRadius: 10 }}
              message={t('ecran.courrielDemande.dernierEchec')}
              description={t('ecran.courrielDemande.echec')} />
          )}
          {apercu.motifAdresse && edition && (
            <Alert type="warning" showIcon style={{ marginBottom: 16, borderRadius: 10 }}
              message={apercu.motifAdresse}
              description={t('ecran.courrielDemande.adresseReelle')} />
          )}

          {edition ? (
            <Form form={form} layout="vertical" requiredMark={false}
              initialValues={{
                destinataireEmail: apercu.motifAdresse ? '' : apercu.email,
                objet: apercu.objet || '',
                corps: apercu.corps || '',
              }}>
              <Form.Item label={t('ecran.courrielDemande.destinataire')} style={{ marginBottom: 8 }}>
                <strong>{vers}</strong>
              </Form.Item>
              <Form.Item name="destinataireEmail" label={t('ecran.courrielDemande.pv.adresseConfirmer')}
                extra={adresseDifferente ? t('ecran.courrielDemande.adresseReferentiel', { email: apercu.email }) : null}
                rules={[
                  { required: true, whitespace: true, message: t('ecran.courrielDemande.adresseRequise') },
                  {
                    validator: (_, v) => (!v || adresseUtilisable(v) ? Promise.resolve()
                      : Promise.reject(new Error(t('ecran.courrielDemande.adresseInvalide'))))
                  },
                ]}>
                <Input placeholder="sinistres@bureau-homologue.ci" />
              </Form.Item>
              <Form.Item name="objet" label={t('ecran.courrielDemande.objet')} rules={[{ required: true, whitespace: true, message: t('ecran.courrielDemande.objetRequis') }]}>
                <Input maxLength={250} />
              </Form.Item>
              <Form.Item name="corps" label={t('ecran.courrielDemande.message')} rules={[{ required: true, whitespace: true, message: t('ecran.courrielDemande.messageRequis') }]}>
                <TextArea autoSize={{ minRows: 10, maxRows: 20 }} />
              </Form.Item>
            </Form>
          ) : (
            <div>
              <div style={{ fontSize: 12, color: '#8c8c8c' }}>{t('ecran.courrielDemande.objet')}</div>
              <div style={{ fontWeight: 600, marginBottom: 12 }}>{apercu.objet}</div>
              <div style={{ fontSize: 12, color: '#8c8c8c' }}>{t('ecran.courrielDemande.message')}</div>
              <div style={{
                whiteSpace: 'pre-wrap', wordBreak: 'break-word', background: '#fafafa',
                border: '1px solid #f0f0f0', borderRadius: 10, padding: 14,
                maxHeight: 420, overflowY: 'auto',
              }}>
                {apercu.corps}
              </div>
            </div>
          )}
        </>
      )}
    </CBModal>
  );
}
